import { type FC, Suspense, lazy, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import { useLocationTab } from "src/hooks/UseLocationTab";
import {
  PAID_MEDIA_TAB_I18N_KEYS,
  PAID_MEDIA_TAB_IDS,
  PAID_MEDIA_TAB_ITEMS,
} from "./PaidMediaTabsDashboard.data";
import "./PaidMediaDashboard.scss";

const PaidMediaOverviewDashboard = lazy(
  () => import("../overview/PaidMediaOverviewDashboard")
);
const MetaAdsReport = lazy(() => import("./MetaAdsReport"));
const GoogleAdsReport = lazy(() => import("./GoogleAdsReport"));

const PaidMediaTabsDashboard: FC = () => {
  const { t } = useTranslation();
  const defaultEndDate = dayjs().format("YYYY-MM-DD");
  const defaultStartDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  const [activeTab, setActiveTab] = useLocationTab(PAID_MEDIA_TAB_IDS, "overview");
  const [appliedStartDate, setAppliedStartDate] = useState(defaultStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState(defaultEndDate);
  const [draftStartDate, setDraftStartDate] = useState(defaultStartDate);
  const [draftEndDate, setDraftEndDate] = useState(defaultEndDate);

  const dateFilters = useMemo(
    () => ({
      appliedStartDate,
      appliedEndDate,
      draftStartDate,
      draftEndDate,
      onApplyDates: ({
        startDate,
        endDate,
      }: {
        startDate: string;
        endDate: string;
      }) => {
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
      },
    }),
    [appliedEndDate, appliedStartDate, draftEndDate, draftStartDate]
  );

  return (
    <div className="paid-media-tabs-dashboard">
      <div className="paid-media-tabs-dashboard__tabs">
        {PAID_MEDIA_TAB_ITEMS?.map(tab => (
          <button
            key={tab?.id}
            type="button"
            className={`paid-media-tabs-dashboard__tab ${
              activeTab === tab?.id ? "paid-media-tabs-dashboard__tab--active" : ""
            }`}
            onClick={() => setActiveTab(tab?.id)}
          >
            {t(PAID_MEDIA_TAB_I18N_KEYS[tab?.id])}
          </button>
        ))}
      </div>

      <div className="paid-media-dashboard">
        <Suspense
          fallback={
            <div className="paid-media-tabs-dashboard__loading" role="status" aria-live="polite">
              <CircularLoader size={32} thickness={4} />
            </div>
          }
        >
          {activeTab === "overview" ? <PaidMediaOverviewDashboard /> : null}
          {activeTab === "meta-ads-report" ? <MetaAdsReport dateFilters={dateFilters} /> : null}
          {activeTab === "google-ads-report" ? (
            <GoogleAdsReport dateFilters={dateFilters} />
          ) : null}
          {activeTab === "tiktok-ads-report" ? (
            <div className="paid-media-tabs-dashboard__coming-soon">
              {t("metaAdsReport.paidMediaTabs.comingSoon")}
            </div>
          ) : null}
        </Suspense>
      </div>
    </div>
  );
};

export default PaidMediaTabsDashboard;
